interface ProfileData {
  profileName: string;
  collections: string[];
  includeHistory: boolean;
  systemInstruction: string;
}

interface Profile {
  profile_id: string;
  backend_config: {
    collections: string[];
    prompt: Array<[string, string]>;
  };
}

export const sanitizeProfileName = (profileName: string): string => {
  return profileName
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]/g, "_")
    .replace(/_{2,}/g, "_")
    .replace(/^_+|_+$/g, "");
};

export const validateProfileName = (profileName: string): { isValid: boolean; error?: string } => {
  if (!profileName.trim()) {
    return { isValid: false, error: "El nombre del perfil es obligatorio" };
  }

  if (profileName.length > 50) {
    return { isValid: false, error: "El nombre del perfil no puede exceder 50 caracteres" };
  }

  if (profileName.length < 2) {
    return { isValid: false, error: "El nombre del perfil debe tener al menos 2 caracteres" };
  }

  const sanitized = sanitizeProfileName(profileName);
  if (!sanitized) {
    return { isValid: false, error: "El nombre del perfil debe contener al menos una letra o número" };
  }

  return { isValid: true };
};

export const formatPromptsForAPI = (profileData: ProfileData): Array<[string, string]> => {
  const formattedPrompts: Array<[string, string]> = [];

  if (profileData.includeHistory) {
    formattedPrompts.push(["placeholder", "{history}"]);
  }

  formattedPrompts.push(["system", "{context}"]);

  if (profileData.systemInstruction.trim()) {
    formattedPrompts.push(["system", profileData.systemInstruction.trim()]);
  }

  formattedPrompts.push(["human", "{question}"]);

  return formattedPrompts;
};

export const parsePromptsFromAPI = (prompts: Array<[string, string]>): {
  includeHistory: boolean;
  systemInstruction: string;
} => {
  const historyPrompt = prompts.find(
    ([type, content]) => type === "placeholder" && content === "{history}"
  );
  
  const systemPrompt = prompts.find(
    ([type, content]) => type === "system" && content !== "{context}"
  );
  
  return {
    includeHistory: !!historyPrompt,
    systemInstruction: systemPrompt ? systemPrompt[1] : "",
  };
};

export const getUniqueCollections = (collections: string[]): string[] => {
  return [...new Set(collections.filter(collection => collection.trim() !== ""))];
};

export const checkProfileNameExists = (
  profileName: string, 
  existingProfiles: Profile[], 
  excludeId?: string
): boolean => {
  const sanitizedName = sanitizeProfileName(profileName);
  
  return existingProfiles.some(profile => 
    profile.profile_id === sanitizedName && profile.profile_id !== excludeId
  );
};

export const validateCollections = (collections: string[]): { isValid: boolean; error?: string } => {
  const uniqueCollections = getUniqueCollections(collections);
  
  if (uniqueCollections.length === 0) {
    return { isValid: false, error: "Debe seleccionar al menos una colección" };
  }

  if (uniqueCollections.length > 20) {
    return { isValid: false, error: "No puede seleccionar más de 20 colecciones" };
  }

  return { isValid: true };
};

export const createProfileForAPI = (profileData: ProfileData, profileId?: string): any => {
  const sanitizedId = profileId || sanitizeProfileName(profileData.profileName);
  const uniqueCollections = getUniqueCollections(profileData.collections);
  const formattedPrompts = formatPromptsForAPI(profileData);

  return {
    profile_id: sanitizedId,
    backend_config: {
      collections: uniqueCollections,
      prompt: formattedPrompts,
    },
  };
};

export const generateProfileSummary = (profileData: ProfileData): {
  name: string;
  collectionsCount: number;
  collections: string[];
  hasHistory: boolean;
  hasCustomInstructions: boolean;
  instructionPreview: string;
} => {
  const uniqueCollections = getUniqueCollections(profileData.collections);
  
  return {
    name: profileData.profileName.trim(),
    collectionsCount: uniqueCollections.length,
    collections: uniqueCollections,
    hasHistory: profileData.includeHistory,
    hasCustomInstructions: !!profileData.systemInstruction.trim(),
    instructionPreview: profileData.systemInstruction.trim().substring(0, 100) + 
      (profileData.systemInstruction.trim().length > 100 ? "..." : ""),
  };
};